import { createContext, useEffect, useState } from 'react';
import { createTheme as createMuiTheme, responsiveFontSizes, StyledEngineProvider, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

import breakpoints from 'theme/breakpoints';
import shape from 'theme/shape';
import typography from 'theme/typography';
import componentsOverride from 'theme/overrides';
import darkPalette from 'theme/palette/dark';
import lightPalette from 'theme/palette/light';
import darkShadows from 'theme/shadows/dark';
import lightShadows from 'theme/shadows/light';

const initialTheme = { scheme: 'light' };

const createTheme = (theme) => {
  const themeOptions = { breakpoints, shape, typography };
  themeOptions.palette = theme.scheme === 'dark' ? darkPalette : lightPalette;
  themeOptions.shadows = theme.scheme === 'dark' ? darkShadows : lightShadows;
  themeOptions.components = componentsOverride(themeOptions);
  return responsiveFontSizes(createMuiTheme(themeOptions));
};

const storeTheme = (theme) => {
  localStorage.setItem('THEME', JSON.stringify(theme));
};

const restoreTheme = () => {
  let theme = null;
  const storedTheme = localStorage.getItem('THEME');

  if (storedTheme) {
    theme = JSON.parse(storedTheme);
  } else {
    const scheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    theme = { scheme };
  }

  return theme;
};

const updateBodyTheme = (theme) => {
  document.body.classList.remove('light', 'dark');
  document.body.classList.add(theme.scheme);
};

export const ThemeContext = createContext({
  theme: initialTheme,
  saveTheme: () => {},
});

export const ThemeProvider = (props) => {
  const { children } = props;
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    const restoredTheme = restoreTheme();

    if (restoredTheme) {
      setTheme(restoredTheme);
      updateBodyTheme(restoredTheme);
    }
  }, []);

  const saveTheme = (theme) => {
    setTheme(theme);
    storeTheme(theme);
    updateBodyTheme(theme);
  };

  return (
    <ThemeContext.Provider value={{ theme, saveTheme }}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={createTheme(theme)}>
          {children}
        </MuiThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
};
